import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  usageEnforcementService,
} from '../services/usage-enforcement.service';
import type {
  IKlaxUsageMetadata,
  IUsageEnforcementPayload,
} from '../types/usage-enforcement.model';

// One tenant per deployment here, so the keys need no tenant id of their own.
const ROOT = ['subscription', 'usage-enforcement'] as const;
const tenantKey = [...ROOT, 'tenant'] as const;
const configuredOrganizationsKey = [...ROOT, 'configured-organizations'] as const;
const organizationKey = (organizationId: string) =>
  [...ROOT, 'organization', organizationId] as const;

export const useUsageEnforcement = () =>
  useQuery({
    queryKey: tenantKey,
    queryFn: () => usageEnforcementService.getEnforcement(),
    staleTime: 10 * 1000,
  });

export interface TenantSave {
  payload: IUsageEnforcementPayload;
  metadata: IKlaxUsageMetadata;
}

/**
 * The tenant screen saves two halves: the platform's switches through the PUT, and
 * Klax's windows and categories into the tenant's metadata through the upsert.
 */
export const useSaveUsageEnforcement = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: tenantKey,
    mutationFn: async ({ payload, metadata }: TenantSave) => {
      await usageEnforcementService.saveEnforcement(payload);
      await usageEnforcementService.upsertRules([{ scope: 'tenant', metadata }]);
    },
    // Every organization without settings of its own now answers differently.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ROOT }),
  });
};

export const useConfiguredOrganizations = () =>
  useQuery({
    queryKey: configuredOrganizationsKey,
    queryFn: () => usageEnforcementService.getConfiguredOrganizations(),
  });

export const useOrganizationEnforcement = (organizationId: string) =>
  useQuery({
    queryKey: organizationKey(organizationId),
    queryFn: () => usageEnforcementService.getOrganizationEnforcement(organizationId),
    enabled: Boolean(organizationId),
  });

export const useSaveOrganizationEnforcement = (organizationId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: organizationKey(organizationId),
    mutationFn: (payload: IUsageEnforcementPayload) =>
      usageEnforcementService.saveOrganizationEnforcement(organizationId, payload),
    onSuccess: (config) => {
      queryClient.setQueryData(organizationKey(organizationId), config);
      queryClient.invalidateQueries({ queryKey: configuredOrganizationsKey });
      // useFreeAccess caches this org's switch for the whole login; an admin saving it must see it now.
      queryClient.invalidateQueries({ queryKey: ['free-access', organizationId] });
    },
  });
};

